import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { COLLECTIONS } from "./siteData";
import { useAdminImages } from "./AboutPremium";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_KEY = "nayara_site_token";
const hdr = () => { const t = localStorage.getItem(SITE_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };
const EASE = [0.22, 1, 0.36, 1];

const TABS = [
  { key: "fabrics", name: "Fabrics", desc: "Printed fashion, kurti & dress materials" },
  { key: "patterns", name: "Patterns", desc: "Ready-to-print signature designs" },
  { key: "backdrops", name: "Backdrops", desc: "Photo, wedding & stage backdrops" },
  { key: "customized", name: "Customized", desc: "Your artwork, our cloth" },
];
const STATIC = {
  fabrics: ["fashion-fabrics", "kurti-fabrics", "dress-materials", "event-fabrics"],
  backdrops: ["photography-backdrops", "wedding-backdrops", "stage-backdrops"],
  customized: ["custom-printing"],
  patterns: [],
};
const staticItems = (key) => STATIC[key].map((slug) => COLLECTIONS.find((c) => c.slug === slug)).filter(Boolean)
  .map((c) => ({ id: c.slug, title: c.name, meta: c.tagline, img: c.image, link: `/collections/${c.slug}` }));

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Caveat:wght@400;600&display=swap');
@keyframes cs-shine{0%{background-position:150% 0}100%{background-position:-50% 0}}
.cs-shine{display:inline-block;padding:0 .15em;background:linear-gradient(110deg,#8a5a32 35%,#f3d6a4 50%,#8a5a32 65%);background-size:250% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:cs-shine 3.5s linear infinite}
.cs-hand{font-family:'Caveat',cursive}
.cs-tab{clip-path:polygon(0 0,100% 0,100% 100%,94% 92%,88% 100%,82% 92%,76% 100%,70% 92%,64% 100%,58% 92%,52% 100%,46% 92%,40% 100%,34% 92%,28% 100%,22% 92%,16% 100%,10% 92%,4% 100%,0 92%)}
.cs-card:hover .cs-img{transform:scale(1.08)}
.cs-card:hover .cs-enq{opacity:1;transform:none}
@media (prefers-reduced-motion:reduce){.cs-shine{animation:none}}
`;

function AddForm({ tab, onClose, onDone }) {
  const [f, setF] = useState({ title: "", meta: "", collection: tab });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!file || !f.title) return alert("Photo & name venum");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v)); fd.append("image", file);
    try { await axios.post(`${API}/site/collection-items`, fd, { headers: hdr() }); onDone(); onClose(); }
    catch (e) { alert(e.response?.data?.detail || "Save aagala"); }
    finally { setBusy(false); }
  };
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="w-full max-w-md bg-[#FAF9F6] p-7" initial={{ y: 40 }} animate={{ y: 0 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-3xl">Add item</h3>
        <label className="mt-5 flex h-40 cursor-pointer items-center justify-center border-2 border-dashed border-[#c9a27a] bg-[#f6efe5] text-sm text-[#8a5a32]">
          {file ? file.name : "+ Photo upload"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <input className="mt-4 w-full border border-[#e2d5c3] bg-white px-3 py-2.5" placeholder="Name (e.g. Georgette Floral)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <input className="mt-3 w-full border border-[#e2d5c3] bg-white px-3 py-2.5" placeholder='Type / size (e.g. Kurti · 44")' value={f.meta} onChange={(e) => setF({ ...f, meta: e.target.value })} />
        <select className="mt-3 w-full border border-[#e2d5c3] bg-white px-3 py-2.5" value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })}>
          {TABS.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
        </select>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="rounded-full bg-[#2b1d16] px-6 py-2.5 text-xs tracking-[0.25em] text-white">{busy ? "SAVING..." : "SAVE"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function CollectionsShowcase() {
  const { isAdmin } = useAdminImages();
  const [tab, setTab] = useState("fabrics");
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => axios.get(`${API}/site/collection-items`, { params: isAdmin ? { all: 1 } : {} })
    .then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => { load(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const byTab = useMemo(() => {
    const out = {};
    TABS.forEach((t) => {
      const db = (items || []).filter((x) => x.collection === t.key && (isAdmin || !x.hidden))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((x) => ({ ...x, img: `${API}/site/fabric-images/${x.image}`, db: true }));
      out[t.key] = db.length ? db : staticItems(t.key);
    });
    return out;
  }, [items, isAdmin]);
  const list = byTab[tab] || [];
  const shown = list.slice(0, 5);

  const act = async (fn) => { try { await fn(); load(); } catch (e) { alert(e.response?.data?.detail || "Aagala"); } };
  const edit = (it) => {
    const title = window.prompt("Name", it.title); if (title === null) return;
    const meta = window.prompt("Type / size", it.meta || ""); if (meta === null) return;
    act(() => axios.put(`${API}/site/collection-items/${it.id}`, { title, meta }, { headers: hdr() }));
  };
  const move = (it, dir) => {
    const dbl = list.filter((x) => x.db); const i = dbl.findIndex((x) => x.id === it.id); const j = i + dir;
    if (j < 0 || j >= dbl.length) return;
    const ids = dbl.map((x) => x.id); [ids[i], ids[j]] = [ids[j], ids[i]];
    act(() => axios.put(`${API}/site/collection-items/reorder`, { ids }, { headers: hdr() }));
  };
  const hide = (it) => act(() => axios.put(`${API}/site/collection-items/${it.id}`, { hidden: !it.hidden }, { headers: hdr() }));
  const del = (it) => window.confirm(`Delete "${it.title}"?`) && act(() => axios.delete(`${API}/site/collection-items/${it.id}`, { headers: hdr() }));

  return (
    <section className="relative overflow-hidden bg-[#FAF9F6] py-16 lg:py-20" data-testid="collections-showcase">
      <style>{CSS}</style>
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <motion.span initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
              className="inline-flex items-center gap-4 text-[0.7rem] uppercase tracking-[0.32em] text-[#8a5a32]">
              <span className="h-px w-12 bg-[#8a5a32]" />Collections
            </motion.span>
            <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.15, ease: EASE }}
              className="mt-5 font-display text-[clamp(2.2rem,4.6vw,4rem)] font-light leading-[1.08] text-[#1A1A1A]">
              What we <span className="cs-shine" style={{ fontFamily: "'Pinyon Script', cursive", fontSize: "1.3em" }}>manufacture.</span>
            </motion.h2>
          </div>
          <Link to="/collections" data-testid="featured-viewall" className="border-b border-[#8a5a32] pb-1.5 text-xs tracking-[0.3em] text-[#8a5a32]">VIEW ALL COLLECTIONS ↗</Link>
        </div>

        {/* Tabs */}
        <div className="mt-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {TABS.map((t, i) => {
            const on = t.key === tab; const n = byTab[t.key]?.length || 0;
            return (
              <motion.button key={t.key} type="button" onClick={() => setTab(t.key)}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 + i * 0.08, ease: EASE }}
                whileHover={{ y: -4 }} className="cs-tab relative bg-[#f3ece2] px-5 pb-6 pt-5 text-left">
                {on && <motion.span layoutId="cs-active" className="absolute inset-0 bg-[#2b1d16]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
                <span className={`pointer-events-none absolute inset-[6px] bottom-3 border border-dashed ${on ? "border-[#e8c8a0]/50" : "border-[#8a5a32]/35"}`} />
                <span className="relative block">
                  <span className={`text-[10px] tracking-[0.3em] ${on ? "text-[#e8c38f]" : "text-[#B89B72]"}`}>0{i + 1}</span>
                  <span className={`mt-1.5 block font-display text-[clamp(1.5rem,2.2vw,2rem)] ${on ? "text-[#FAF9F6]" : "text-[#1A1A1A]"}`}>{t.name}</span>
                  <span className={`block text-[13px] font-light ${on ? "text-[#d9c6b0]" : "text-[#6b5d52]"}`}>{t.desc}</span>
                  <span className={`cs-hand mt-3 block text-xl ${on ? "text-[#e8c38f]" : "text-[#8a5a32]"}`}>{t.key === "customized" && !n ? "On request" : `${n} designs`}</span>
                </span>
              </motion.button>
            );
          })}
        </div>

        {isAdmin && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 bg-[#2b1d16] px-5 py-3.5 text-[13px] tracking-[0.15em] text-[#f1d7ae]">
            <span>ADMIN MODE · <b className="font-normal text-white">{TABS.find((t) => t.key === tab).name}</b> · {list.filter((x) => x.db).length} items</span>
            <button type="button" onClick={() => setAdding(true)} className="rounded-full bg-[#e8c38f] px-5 py-2 text-xs tracking-[0.25em] text-[#2b1d16]">+ ADD ITEM</button>
          </div>
        )}

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div key={tab} className="mt-5 grid auto-rows-[220px] gap-3.5 md:grid-cols-[1.4fr_1fr_1fr] lg:auto-rows-[260px]"
            initial="hide" animate="show" exit="out" variants={{ show: { transition: { staggerChildren: 0.09 } } }}>
            {shown.length === 0 && (
              <motion.div variants={{ hide: { opacity: 0 }, show: { opacity: 1 }, out: { opacity: 0 } }}
                className="cs-hand col-span-full flex h-[220px] items-center justify-center border-2 border-dashed border-[#c9a27a] text-3xl text-[#8a5a32]">
                New designs coming soon…
              </motion.div>
            )}
            {shown.map((it, i) => (
              <motion.div key={it.id} className={`cs-card group relative overflow-hidden ${i === 0 ? "md:row-span-2" : ""} ${it.hidden ? "opacity-40" : ""}`}
                variants={{
                  hide: { clipPath: "inset(0 0 100% 0)", opacity: 1 },
                  show: { clipPath: "inset(0 0 0% 0)", transition: { duration: 1, ease: [0.7, 0, 0.2, 1] } },
                  out: { opacity: 0, y: -20, transition: { duration: 0.3 } },
                }}>
                <img src={it.img} alt={it.title} className="cs-img absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out" />
                <motion.span aria-hidden="true" className="absolute inset-x-0 z-10 h-3 bg-gradient-to-b from-[#1d1410] to-[#4a3020] shadow-[0_8px_20px_rgba(0,0,0,.45)]"
                  variants={{ hide: { top: "-3%" }, show: { top: "102%", transition: { duration: 1, ease: [0.7, 0, 0.2, 1] } } }} />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#1b130e]/85" />
                <div className="absolute bottom-5 left-5 right-5 text-[#FAF9F6]">
                  {it.meta && <span className="block text-[10.5px] uppercase tracking-[0.3em] text-[#f1d7ae]">{it.meta}</span>}
                  <h4 className={`mt-1.5 font-display ${i === 0 ? "text-[clamp(1.8rem,3vw,2.6rem)]" : "text-2xl"}`}>{it.title}</h4>
                </div>
                <Link to={it.link || "/contact"} className="cs-enq absolute bottom-5 right-5 z-10 translate-y-2 rounded-full border border-white/60 px-3.5 py-2 text-[11px] tracking-[0.2em] text-white opacity-0 transition-all duration-500 hover:bg-white hover:text-[#2b1d16]">
                  {it.link ? "VIEW ↗" : "ENQUIRE ↗"}
                </Link>
                {isAdmin && it.db && (
                  <div className="absolute right-3 top-3 z-20 flex gap-1.5">
                    {[["✎", () => edit(it)], ["↑", () => move(it, -1)], ["↓", () => move(it, 1)], [it.hidden ? "◌" : "👁", () => hide(it)]].map(([l, fn]) => (
                      <button key={l} type="button" onClick={fn} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-sm text-[#2b1d16] shadow">{l}</button>
                    ))}
                    <button type="button" onClick={() => del(it)} className="flex h-8 w-8 items-center justify-center rounded-full bg-[#b5543a] text-sm text-white shadow">🗑</button>
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {list.length > 5 && (
          <div className="cs-hand mt-5 text-right text-2xl text-[#8a5a32]"><Link to="/collections">+{list.length - 5} more designs →</Link></div>
        )}
        {tab === "customized" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-5 flex flex-wrap items-center justify-between gap-4 border border-dashed border-[#c9a27a] px-6 py-5">
            <span className="font-display text-2xl">Have your own artwork? <span className="cs-hand text-[#8a5a32]">we print, stitch &amp; ship.</span></span>
            <Link to="/contact" className="rounded-full bg-[#2b1d16] px-6 py-3 text-xs tracking-[0.25em] text-white">SEND YOUR DESIGN ↗</Link>
          </motion.div>
        )}
      </div>
      <AnimatePresence>{adding && <AddForm tab={tab} onClose={() => setAdding(false)} onDone={load} />}</AnimatePresence>
    </section>
  );
}
